import {
  createContext,
  useEffect,
  type KeyboardEvent,
  type PropsWithChildren,
} from 'react';
import {type FieldErrors, FormProvider} from 'react-hook-form';
import {trimFormSchema} from './trim-form-schema';
import {
  type FormSchema,
  useCompanyRegistrationForm,
  useCompanyRegistrationFormContext,
} from '@/features/company-registration/form';
import {usePostValidateMutation} from '@/features/company-registration/api/mutations/use-post-validate-mutation';
import {convertToCompanyRegistrationFormData} from '@/features/company-registration/lib/convert-to-company-registration-form-data';
import {noticeError} from '@/utils/noticeError';

type Props = {
  readonly submit: (values: FormSchema) => void;
  readonly defaultValues: FormSchema;
  readonly className?: string;
} & PropsWithChildren;

export const companyRegistrationFormSubmittingContext = createContext(false);

export function DiscardModalWrapper({children}: PropsWithChildren) {
  const {
    formState: {isDirty},
  } = useCompanyRegistrationFormContext();
  const unloadCallback = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = ''; // Chrome対応
  };

  useEffect(() => {
    if (!isDirty) return;

    window.addEventListener('beforeunload', unloadCallback);
    return () => {
      window.removeEventListener('beforeunload', unloadCallback);
    };
  }, [isDirty]);

  return children;
}

export function CompanyRegistrationFormRoot({
  submit,
  defaultValues,
  children,
  className,
  ...props
}: Props) {
  const formMethods = useCompanyRegistrationForm(defaultValues);
  const {mutateAsync, isPending} = usePostValidateMutation(formMethods);

  const onSubmit = formMethods.handleSubmit(
    async (data) => {
      data = trimFormSchema(data);
      await mutateAsync(convertToCompanyRegistrationFormData(data));
      submit(data);
    },
    (error) => {
      const fieldErrorsMessage = stringifyFieldErrors(error);

      noticeError('企業登録申請ページ：バリデーションエラー', {
        fieldErrors: fieldErrorsMessage,
      });
    },
  );

  const onKeydown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return (
    <form
      noValidate
      className={className}
      onSubmit={onSubmit}
      onKeyDown={onKeydown}
      {...props}
    >
      <companyRegistrationFormSubmittingContext.Provider value={isPending}>
        <FormProvider {...formMethods}>
          <DiscardModalWrapper>{children}</DiscardModalWrapper>
        </FormProvider>
      </companyRegistrationFormSubmittingContext.Provider>
    </form>
  );
}

const stringifyFieldErrors = (errors: FieldErrors<FormSchema>) => {
  try {
    // react-hook-formのエラーをログに出力する
    let fieldErrorsMessage = '{';

    if (errors.root) {
      fieldErrorsMessage += `root: "${errors.root.message}",`;
    }

    for (const [key, value] of Object.entries(errors)) {
      if (typeof value === 'object') {
        fieldErrorsMessage += `${key}: {`;
        for (const [k, v] of Object.entries(value)) {
          fieldErrorsMessage += `${k}: "${v.message}",`;
        }

        fieldErrorsMessage = fieldErrorsMessage.slice(0, -1);
        fieldErrorsMessage += '},';
        continue;
      }
    }

    fieldErrorsMessage = fieldErrorsMessage.slice(0, -1);
    fieldErrorsMessage += '}';

    return fieldErrorsMessage;
  } catch {
    return 'FieldErrorsのstringifyに失敗しました';
  }
};
